import React, { useEffect, useMemo, useState } from 'react';
import './dash.css';
import { app, firebaseConfig } from '../firebase.js';
import { initializeApp, getApps } from 'firebase/app';
import { createUserWithEmailAndPassword, deleteUser, getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  getFirestore,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import InputGroup from 'react-bootstrap/InputGroup';
import swal from 'sweetalert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleXmark,
  faMagnifyingGlass,
  faPenToSquare,
  faPlus,
  faShieldHalved,
  faTrash,
  faUserShield,
} from '@fortawesome/free-solid-svg-icons';

const defaultForm = {
  userID: '',
  userName: '',
  userEmail: '',
  phone: '',
  password: '',
  adminType: 'admin',
  accessStatus: 'active',
};

const roleOptions = [
  { value: 'superAdmin', label: 'Super Admin' },
  { value: 'admin', label: 'Admin' },
  { value: 'viewer', label: 'Viewer' },
];

const normalizeAdminType = (value) => {
  const normalized = String(value || 'admin').trim().toLowerCase();
  if (normalized === 'superadmin') return 'superAdmin';
  if (normalized === 'viewer') return 'viewer';
  return 'admin';
};

function AdminsComp() {
  const db = getFirestore(app);
  const auth = getAuth();
  const secondaryApp = (() => {
    const existing = getApps().find((firebaseApp) => firebaseApp.name === 'admins-secondary');
    return existing || initializeApp(firebaseConfig, 'admins-secondary');
  })();
  const secondaryAuth = getAuth(secondaryApp);

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [accessFilter, setAccessFilter] = useState('all');
  const [formData, setFormData] = useState(defaultForm);

  const loadAdmins = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'Admins'));
      const items = snap.docs.map((adminDoc) => ({
        id: adminDoc.id,
        ...adminDoc.data(),
      }));

      items.sort((a, b) => {
        const aTime = a?.createdAt?.seconds ? a.createdAt.seconds : 0;
        const bTime = b?.createdAt?.seconds ? b.createdAt.seconds : 0;
        return bTime - aTime || String(a.userName || '').localeCompare(String(b.userName || ''));
      });

      setAdmins(items);
    } catch (error) {
      console.error('Error loading admins:', error);
      swal('Error', 'Failed to load admin records.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setAdmins([]);
        setLoading(false);
        return;
      }

      loadAdmins();
    });

    return () => unsubscribe();
  }, [auth]);

  const filteredAdmins = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return admins.filter((admin) => {
      const haystack = [
        admin.userName,
        admin.userEmail,
        admin.phone,
        admin.userID,
        admin.adminType,
        admin.accessStatus,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const roleValue = normalizeAdminType(admin.adminType);
      const accessValue = String(admin.accessStatus || 'active').toLowerCase();

      const matchesSearch = !term || haystack.includes(term);
      const matchesRole = roleFilter === 'all' || roleValue === roleFilter;
      const matchesAccess = accessFilter === 'all' || accessValue === accessFilter;

      return matchesSearch && matchesRole && matchesAccess;
    });
  }, [admins, accessFilter, roleFilter, searchTerm]);

  const summary = useMemo(() => {
    const activeCount = admins.filter((admin) => String(admin.accessStatus || 'active').toLowerCase() !== 'disabled').length;
    const disabledCount = admins.filter((admin) => String(admin.accessStatus || '').toLowerCase() === 'disabled').length;
    const superAdminCount = admins.filter((admin) => normalizeAdminType(admin.adminType) === 'superAdmin').length;

    return {
      total: admins.length,
      activeCount,
      disabledCount,
      superAdminCount,
    };
  }, [admins]);

  const resetFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
    setAccessFilter('all');
  };

  const resolveRoleLabel = (value) => {
    const normalized = normalizeAdminType(value);
    const found = roleOptions.find((option) => option.value === normalized);
    return found ? found.label : value || 'Admin';
  };

  const resolveAccessLabel = (value) => {
    const normalized = String(value || 'active').toLowerCase();
    return normalized === 'disabled' ? 'Disabled' : 'Active';
  };

  const openCreateModal = () => {
    setEditingAdmin(null);
    setFormData(defaultForm);
    setShowModal(true);
  };

  const openEditModal = (admin) => {
    setEditingAdmin(admin);
    setFormData({
      userID: admin.userID || admin.id || '',
      userName: admin.userName || '',
      userEmail: admin.userEmail || '',
      phone: admin.phone || '',
      password: '',
      adminType: normalizeAdminType(admin.adminType),
      accessStatus: admin.accessStatus || 'active',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingAdmin(null);
    setFormData(defaultForm);
  };

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const createAdminAuthAccount = async () => {
    const userCredential = await createUserWithEmailAndPassword(
      secondaryAuth,
      formData.userEmail.trim(),
      formData.password
    );

    return userCredential;
  };

  const handleSaveAdmin = async (event) => {
    event.preventDefault();

    const userName = formData.userName.trim();
    const userEmail = formData.userEmail.trim();
    const phone = formData.phone.trim();
    const adminType = normalizeAdminType(formData.adminType);
    const accessStatus = formData.accessStatus.trim() || 'active';

    if (!userName || !userEmail || !phone) {
      swal('Missing details', 'Please fill in the name, email, and phone number.', 'warning');
      return;
    }

    if (!editingAdmin && !formData.password) {
      swal('Missing password', 'Please set an initial password for the new admin account.', 'warning');
      return;
    }

    setSaving(true);

    let createdUser = null;
    const uidToUse = editingAdmin ? formData.userID.trim() : '';

    try {
      if (!editingAdmin) {
        createdUser = await createAdminAuthAccount();
      }

      const resolvedUserID = editingAdmin ? uidToUse : createdUser?.user?.uid;

      if (!resolvedUserID) {
        throw new Error('Failed to resolve the admin UID.');
      }

      const docRef = doc(db, 'Admins', editingAdmin?.id || resolvedUserID);
      const existingDoc = editingAdmin ? admins.find((admin) => admin.id === editingAdmin.id) : null;

      await setDoc(docRef, {
        userID: resolvedUserID,
        userName,
        userEmail,
        phone,
        adminType,
        accessStatus,
        createdAt: existingDoc?.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
        docId: editingAdmin?.id || resolvedUserID,
      }, { merge: true });

      await loadAdmins();
      closeModal();
      swal(
        'Success',
        editingAdmin ? 'Admin access record updated successfully.' : 'Admin account created successfully.',
        'success'
      );
    } catch (error) {
      console.error('Error saving admin:', error);

      if (createdUser?.user) {
        try {
          await deleteUser(createdUser.user);
        } catch (cleanupError) {
          console.error('Failed to clean up auth account:', cleanupError);
        }
      }

      swal('Error', error?.message || 'Failed to save admin.', 'error');
    } finally {
      await signOut(secondaryAuth).catch(() => {});
      setSaving(false);
    }
  };

  const toggleAdminAccess = async (admin) => {
    const nextStatus = String(admin.accessStatus || 'active').toLowerCase() === 'disabled' ? 'active' : 'disabled';

    try {
      await updateDoc(doc(db, 'Admins', admin.id), {
        accessStatus: nextStatus,
        updatedAt: serverTimestamp(),
      });
      await loadAdmins();
      swal('Updated', `Admin access ${nextStatus === 'active' ? 'restored' : 'disabled'}.`, 'success');
    } catch (error) {
      console.error('Error updating admin access:', error);
      swal('Error', 'Failed to update admin access.', 'error');
    }
  };

  const handleDeleteAdmin = async (admin) => {
    const confirmDelete = await swal({
      title: 'Revoke admin access?',
      text: `${admin.userName || 'This admin'} will lose platform access immediately.`,
      icon: 'warning',
      buttons: ['Cancel', 'Revoke'],
      dangerMode: true,
    });

    if (!confirmDelete) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'Admins', admin.id));
      await loadAdmins();
      swal('Revoked', 'Admin access record removed successfully.', 'success');
    } catch (error) {
      console.error('Error deleting admin:', error);
      swal('Error', 'Failed to revoke admin access.', 'error');
    }
  };

  const formatDate = (value) => {
    if (!value) return 'N/A';
    if (value?.toDate) {
      return value.toDate().toLocaleString();
    }
    if (value?.seconds) {
      return new Date(value.seconds * 1000).toLocaleString();
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? 'N/A' : parsed.toLocaleString();
  };

  const toDateValue = (value) => {
    if (!value) return null;
    if (value?.toDate) return value.toDate();
    if (value?.seconds) return new Date(value.seconds * 1000);
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatRelativeTime = (value) => {
    const dateValue = toDateValue(value);
    if (!dateValue) return null;

    const diffMs = Date.now() - dateValue.getTime();
    if (diffMs < 60 * 1000) return 'just now';
    if (diffMs < 60 * 60 * 1000) return `${Math.floor(diffMs / (60 * 1000))}m ago`;
    if (diffMs < 24 * 60 * 60 * 1000) return `${Math.floor(diffMs / (60 * 60 * 1000))}h ago`;
    if (diffMs < 7 * 24 * 60 * 60 * 1000) return `${Math.floor(diffMs / (24 * 60 * 60 * 1000))}d ago`;
    return formatDate(value);
  };

  return (
    <div className="teamsComponent adminsPage">
      <div className="adminsHero">
        <div className="adminsHeroCopy">
          <div className="adminsEyebrow">
            <FontAwesomeIcon icon={faUserShield} />
            <span>Platform access control</span>
          </div>
          <h5>Admin Management</h5>
          <p>
            Create, update, and pause platform admins from one workspace. Every new admin is linked to Firebase Auth
            and a matching document in the <code>Admins</code> collection.
          </p>
          <div className="adminsHeroActions">
            <button className="startOnlSessBtn adminsCreateBtn" onClick={openCreateModal}>
              <FontAwesomeIcon icon={faPlus} className="liveSessionIcon" /> Add Admin
            </button>
            <div className="adminsHeroSubcopy">
              <FontAwesomeIcon icon={faShieldHalved} />
              <span>Disable access without deleting the admin record.</span>
            </div>
          </div>
        </div>

        <div className="adminsHeroPanel">
          <div className="adminsHeroPanelTop">
            <span>Access snapshot</span>
            <strong>{summary.total}</strong>
          </div>
          <div className="adminsHeroMetricGrid">
            <div className="adminsHeroMetric">
              <span>Active</span>
              <strong>{summary.activeCount}</strong>
            </div>
            <div className="adminsHeroMetric">
              <span>Disabled</span>
              <strong>{summary.disabledCount}</strong>
            </div>
            <div className="adminsHeroMetric">
              <span>Super Admins</span>
              <strong>{summary.superAdminCount}</strong>
            </div>
            <div className="adminsHeroMetric">
              <span>Visible</span>
              <strong>{filteredAdmins.length}</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="adminsFiltersBar">
        <InputGroup className="adminsSearch">
          <InputGroup.Text>
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </InputGroup.Text>
          <Form.Control
            placeholder="Search name, email, phone, or role"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </InputGroup>

        <Form.Select className="adminsFilterSelect" value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)}>
          <option value="all">All roles</option>
          {roleOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Select>

        <Form.Select className="adminsFilterSelect" value={accessFilter} onChange={(event) => setAccessFilter(event.target.value)}>
          <option value="all">All access</option>
          <option value="active">Active</option>
          <option value="disabled">Disabled</option>
        </Form.Select>

        <Button variant="outline-secondary" className="adminsResetBtn" onClick={resetFilters}>
          Reset
        </Button>
      </div>

      <div className="adminsGrid">
        <section className="adminsMainCard">
          <div className="adminsTableHeader">
            <div>
              <h6>Admin roster</h6>
              <p>
                {loading ? 'Loading admin data...' : `${filteredAdmins.length} visible • ${admins.length} total`}
              </p>
            </div>
            <div className="adminsHeaderBadges">
              <span className="adminsHeaderBadge adminsHeaderBadgeSuccess">
                Active {summary.activeCount}
              </span>
              <span className="adminsHeaderBadge adminsHeaderBadgeMuted">
                Disabled {summary.disabledCount}
              </span>
            </div>
          </div>

          {loading ? (
            <div className="spinner-container adminsLoadingState">
              <Spinner animation="border" role="status" />
              <span>Loading admins...</span>
            </div>
          ) : filteredAdmins.length === 0 ? (
            <div className="adminsEmptyState">
              <div className="adminsEmptyBadge">
                <FontAwesomeIcon icon={faUserShield} />
              </div>
              <h6>No admin records match your filters.</h6>
              <p>Try a different search, clear the filters, or create a new admin account.</p>
              <Button className="adminsEmptyBtn" onClick={openCreateModal}>
                <FontAwesomeIcon icon={faPlus} className="liveSessionIcon" /> Add Admin
              </Button>
            </div>
          ) : (
            <div className="table-responsive adminsTableWrap">
              <Table hover className="align-middle adminsTable">
                <thead>
                  <tr>
                    <th>Admin</th>
                    <th>Phone</th>
                    <th>Role</th>
                    <th>Access</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAdmins.map((admin) => {
                    const isDisabled = String(admin.accessStatus || 'active').toLowerCase() === 'disabled';
                    const isSuperAdmin = normalizeAdminType(admin.adminType) === 'superAdmin';

                    return (
                      <tr key={admin.id}>
                        <td>
                          <div className="adminsNameCell">
                            <div className="adminsNameAvatar">
                              {String(admin.userName || 'A').charAt(0).toUpperCase()}
                            </div>
                            <div className="adminsNameDetails">
                              <strong>{admin.userName || 'Unnamed admin'}</strong>
                              <div className="adminsContactText">{admin.userEmail || 'Email not set'}</div>
                              <div className="adminsMiniText" title={admin.lastLoginAt ? formatDate(admin.lastLoginAt) : 'No login recorded yet'}>
                                Last login: {formatRelativeTime(admin.lastLoginAt) || 'Never logged in'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>{admin.phone || 'N/A'}</td>
                        <td>
                          <span className={`adminsPill ${isSuperAdmin ? 'adminsPillDark' : 'adminsPillSoft'}`}>
                            {resolveRoleLabel(admin.adminType)}
                          </span>
                        </td>
                        <td>
                          <div className="adminsStatusCell">
                            <span className={`adminsPill ${isDisabled ? 'adminsPillMuted' : 'adminsPillSuccess'}`}>
                              {resolveAccessLabel(admin.accessStatus)}
                            </span>
                            <div className="adminsStatusMeta" title={admin.lastActiveAt ? formatDate(admin.lastActiveAt) : 'No activity recorded yet'}>
                              Last active: {formatRelativeTime(admin.lastActiveAt) || 'No activity yet'}
                            </div>
                          </div>
                        </td>
                        <td>{formatDate(admin.createdAt)}</td>
                        <td>
                          <div className="adminsActions">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => openEditModal(admin)}
                            >
                              <FontAwesomeIcon icon={faPenToSquare} /> Edit
                            </Button>
                            <Button
                              variant={isDisabled ? 'outline-success' : 'outline-warning'}
                              size="sm"
                              onClick={() => toggleAdminAccess(admin)}
                            >
                              <FontAwesomeIcon icon={isDisabled ? faCircleCheck : faCircleXmark} />{' '}
                              {isDisabled ? 'Enable' : 'Disable'}
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteAdmin(admin)}
                            >
                              <FontAwesomeIcon icon={faTrash} /> Revoke
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </section>
      </div>

      <Modal show={showModal} onHide={closeModal} centered>
        <Form onSubmit={handleSaveAdmin}>
          <Modal.Header closeButton className="adminsModalHeader">
            <Modal.Title>{editingAdmin ? 'Edit Admin' : 'Create Admin'}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="adminsModalBody">
            <div className="adminsModalIntro">
              <div className="adminsModalIntroIcon">
                <FontAwesomeIcon icon={faUserShield} />
              </div>
              <div>
                <strong>{editingAdmin ? 'Update an existing admin' : 'Create a new access record'}</strong>
                <span>
                  {editingAdmin
                    ? 'Adjust the admin profile, role, or access state.'
                    : 'A Firebase Auth account will be created and linked automatically.'}
                </span>
              </div>
            </div>

            {editingAdmin ? (
              <Form.Group className="mb-3">
                <Form.Label>Firebase Auth UID</Form.Label>
                <Form.Control
                  type="text"
                  name="userID"
                  value={formData.userID}
                  onChange={handleFieldChange}
                  disabled
                  readOnly
                />
                <Form.Text className="text-muted">
                  This UID comes from Firebase Auth and cannot be changed here.
                </Form.Text>
              </Form.Group>
            ) : (
              <div className="adminsHintBox mb-3">
                <strong>UID will be generated automatically.</strong>
                <span>
                  Create the Firebase Auth account with the email and password below, then the platform access record
                  will be linked to that new UID.
                </span>
              </div>
            )}

            <div className="adminsModalGrid">
              <Form.Group className="mb-3">
                <Form.Label>Full Name</Form.Label>
                <Form.Control
                  type="text"
                  name="userName"
                  value={formData.userName}
                  onChange={handleFieldChange}
                  placeholder="Enter admin name"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="userEmail"
                  value={formData.userEmail}
                  onChange={handleFieldChange}
                  placeholder="Enter admin email"
                  disabled={Boolean(editingAdmin)}
                  required
                />
                {editingAdmin && (
                  <Form.Text className="text-muted">
                    The Firebase Auth email should be changed separately if needed.
                  </Form.Text>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Phone Number</Form.Label>
                <Form.Control
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleFieldChange}
                  placeholder="Enter phone number"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Admin Role</Form.Label>
                <Form.Select name="adminType" value={formData.adminType} onChange={handleFieldChange}>
                  {roleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3 adminsModalFullWidth">
                <Form.Label>Access Status</Form.Label>
                <Form.Select name="accessStatus" value={formData.accessStatus} onChange={handleFieldChange}>
                  <option value="active">active</option>
                  <option value="disabled">disabled</option>
                </Form.Select>
              </Form.Group>

              {!editingAdmin && (
                <Form.Group className="mb-3 adminsModalFullWidth">
                  <Form.Label>Temporary Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleFieldChange}
                    placeholder="Create a password"
                    required={!editingAdmin}
                  />
                </Form.Group>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer className="adminsModalFooter">
            <Button variant="secondary" onClick={closeModal} disabled={saving}>
              Close
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" role="status" aria-hidden="true" /> Saving...
                </>
              ) : editingAdmin ? (
                'Update Admin'
              ) : (
                'Create Admin'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default AdminsComp;
